import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShoppingCart,
  faHeart,
  faMinus,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartEmpty } from '@fortawesome/free-regular-svg-icons';
import { API_BASE_URL } from '../config/api.config';
import { formatProduct } from '../utils/categories';
import { addProductToCart, toggleProductFavorite, isGuestFavorite, getStoredUser } from '../utils/guestStore';
import BackButton from './BackButton';
import { useToast } from './Toast';
import './ProductDetailPage.css';

function getCategoryBackUrl(catId, page = 1) {
  if (!catId) return '/';
  return page > 1 ? `/category/${catId}?page=${page}` : `/category/${catId}`;
}

const ProductDetailPage = ({ onCartUpdate }) => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);

  const fromCategory = useMemo(() => {
    const fromState = location.state?.fromCategory;
    const fromQuery = searchParams.get('fromCategory');
    const n = Number(fromState || fromQuery);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [location.state, searchParams]);

  const fromPage = useMemo(() => {
    const fromState = location.state?.fromPage;
    const fromQuery = searchParams.get('fromPage');
    const n = Number(fromState || fromQuery || 1);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }, [location.state, searchParams]);

  const categoryNameFromState = location.state?.categoryName || '';

  const resolveBackTarget = (loadedProduct) => {
    const catId = fromCategory || loadedProduct?.categoryId;
    if (!catId) return null;
    const page =
      fromCategory && (!loadedProduct?.categoryId || fromCategory === loadedProduct.categoryId)
        ? fromPage
        : 1;
    return {
      catId,
      url: getCategoryBackUrl(catId, page),
      name: categoryNameFromState || loadedProduct?.categoryName || 'category',
    };
  };

  const backTarget = resolveBackTarget(product);

  const handleBack = () => {
    const target = resolveBackTarget(product);
    if (target) {
      navigate(target.url);
      return;
    }
    navigate('/');
  };

  useEffect(() => {
    if (!getStoredUser()) {
      setIsFavorite(isGuestFavorite(Number(productId)));
    }
  }, [productId]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}?action=product&id=${productId}`);
        const data = await response.json();
        if (!cancelled) {
          if (data.status === 'ok' && data.product) {
            setProduct(formatProduct(data.product));
          } else {
            setProduct(null);
          }
        }
      } catch (err) {
        console.error('Error loading product:', err);
        if (!cancelled) setProduct(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [productId]);

  const handleAddToCart = async () => {
    try {
      const result = await addProductToCart(product, quantity);
      if (result.ok) {
        toast.success(`Added ${quantity} to cart`);
        if (onCartUpdate) onCartUpdate();
      } else {
        toast.error(result.message || 'Failed to add to cart');
      }
    } catch (err) {
      console.error('Add to cart error:', err);
      toast.error('Error adding to cart');
    }
  };

  const handleAddToFavorites = async () => {
    try {
      const result = await toggleProductFavorite(product, isFavorite);
      if (result.ok) {
        setIsFavorite(result.isFavorite);
        toast.success(result.isFavorite ? 'Added to favourites' : 'Removed from favourites');
      } else {
        toast.error(result.message || 'Failed to update favorites');
      }
    } catch (err) {
      console.error('Favorites error:', err);
      toast.error('Error updating favorites');
    }
  };

  const backLabel = backTarget ? `Back to ${backTarget.name}` : 'Back';

  if (loading) {
    return (
      <div className="product-detail-page">
        <BackButton onClick={handleBack} label={backLabel} />
        <div className="product-detail-loading">Loading product…</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-page">
        <BackButton onClick={handleBack} label="Back" />
        <div className="product-detail-loading">Product not found.</div>
      </div>
    );
  }

  const categoryLinkPage =
    fromCategory === product.categoryId ? fromPage : 1;

  return (
    <div className="product-detail-page">
      <BackButton onClick={handleBack} label={backLabel} />
      <div className="product-detail-card">
        <div className="product-detail-image">
          <img src={product.image} alt={product.name} loading="lazy" />
        </div>
        <div className="product-detail-info">
          {product.categoryName && product.categoryId && (
            <button
              type="button"
              className="product-detail-category"
              onClick={() =>
                navigate(getCategoryBackUrl(product.categoryId, categoryLinkPage))
              }
            >
              {product.categoryName}
            </button>
          )}
          <h1>{product.name}</h1>
          {product.description && <p className="product-detail-desc">{product.description}</p>}
          <div className="product-detail-price">Rs. {parseFloat(product.price).toFixed(2)}</div>

          <div className="product-detail-qty">
            <span className="qty-label">Quantity</span>
            <div className="qty-control qty-control-lg">
              <button
                type="button"
                className="qty-btn"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                aria-label="Decrease quantity"
              >
                <FontAwesomeIcon icon={faMinus} />
              </button>
              <span className="qty-value">{quantity}</span>
              <button
                type="button"
                className="qty-btn"
                onClick={() => setQuantity((q) => q + 1)}
                aria-label="Increase quantity"
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>
          </div>

          <div className="product-detail-actions">
            <button type="button" className="detail-add-btn" onClick={handleAddToCart}>
              <FontAwesomeIcon icon={faShoppingCart} /> Add to cart
            </button>
            <button
              type="button"
              className={`detail-fav-btn${isFavorite ? ' active' : ''}`}
              onClick={handleAddToFavorites}
            >
              <FontAwesomeIcon icon={isFavorite ? faHeart : faHeartEmpty} />
              {isFavorite ? 'In favourites' : 'Add to favourites'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
